import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Lock, Menu, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";

function LangToggle({ transparent }) {
    const { lang, setLang } = useI18n();
    return (
        <div
            data-testid="lang-toggle"
            className={`inline-flex items-center text-[10px] tracking-[0.28em] uppercase font-sans-modern transition-colors duration-500 ${
                transparent ? "text-xv-ivory/80" : "text-xv-black/80"
            }`}
        >
            <button
                type="button"
                data-testid="lang-fr"
                onClick={() => setLang("fr")}
                className={`px-1.5 transition-colors duration-300 ${
                    lang === "fr" ? "text-xv-gold" : "opacity-55 hover:opacity-100"
                }`}
            >
                FR
            </button>
            <span
                aria-hidden="true"
                className={`mx-1 ${transparent ? "text-xv-ivory/30" : "text-xv-black/30"}`}
            >
                /
            </span>
            <button
                type="button"
                data-testid="lang-en"
                onClick={() => setLang("en")}
                className={`px-1.5 transition-colors duration-300 ${
                    lang === "en" ? "text-xv-gold" : "opacity-55 hover:opacity-100"
                }`}
            >
                EN
            </button>
        </div>
    );
}

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { t } = useI18n();

    const NAV = [
        { label: t("nav.home"), to: "/" },
        { label: t("nav.about"), to: "/a-propos" },
        { label: t("nav.expertises"), to: "/expertises" },
        { label: t("nav.approach"), to: "/approche" },
        { label: t("nav.ecosystem"), to: "/ecosysteme" },
        { label: t("nav.collectivites"), to: "/collectivites" },
        { label: t("nav.articles"), to: "/articles" },
        { label: t("nav.contact"), to: "/contact" },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    const isHome = location.pathname === "/";
    const transparent = isHome && !scrolled;

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out ${
                transparent
                    ? "bg-transparent border-transparent"
                    : "bg-xv-ivory/92 backdrop-blur-md border-b border-xv-muted"
            }`}
        >
            <div className="max-w-[1500px] mx-auto px-6 md:px-10 flex items-center justify-between h-20 md:h-24">
                <Link
                    to="/"
                    data-testid="brand-logo"
                    className="flex items-center group"
                >
                    <img
                        src={
                            transparent
                                ? "/brand/logo-principal.png"
                                : "/brand/logo-on-light.png"
                        }
                        alt="XV Capital Partners"
                        className="h-12 md:h-14 w-auto opacity-95 group-hover:opacity-100 transition-opacity duration-500"
                    />
                </Link>

                <nav className="hidden md:flex items-center gap-4 lg:gap-6">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-link-${item.to.replace("/", "") || "home"}`}
                            className={({ isActive }) =>
                                `relative whitespace-nowrap text-[10px] lg:text-[10.5px] tracking-[0.18em] lg:tracking-[0.2em] uppercase font-sans-modern transition-colors duration-500 link-underline ${
                                    transparent
                                        ? "text-xv-ivory/90 hover:text-xv-ivory"
                                        : "text-xv-black/85 hover:text-xv-black"
                                } ${isActive ? "!text-xv-gold" : ""}`
                            }
                            end={item.to === "/"}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <LangToggle transparent={transparent} />
                    <span
                        aria-hidden="true"
                        className={`block w-px h-4 ${transparent ? "bg-xv-ivory/25" : "bg-xv-black/15"}`}
                    />
                    <Link
                        to="/espace-prive"
                        data-testid="navbar-private-access"
                        className={`group inline-flex items-center gap-2 px-4 py-3 whitespace-nowrap text-[10px] tracking-[0.22em] uppercase transition-all duration-700 ${
                            transparent
                                ? "text-xv-ivory/80 hover:text-xv-ivory"
                                : "text-xv-midnight/85 hover:text-xv-midnight"
                        }`}
                    >
                        <Lock strokeWidth={1.2} size={12} />
                        {t("nav.private")}
                    </Link>
                    <Link
                        to="/contact"
                        data-testid="navbar-cta"
                        className={`group inline-flex items-center gap-3 px-4 py-3 whitespace-nowrap text-[10px] tracking-[0.22em] uppercase transition-all duration-700 ${
                            transparent
                                ? "border border-xv-ivory/40 text-xv-ivory hover:bg-xv-gold hover:border-xv-gold hover:text-xv-black"
                                : "border border-xv-midnight text-xv-midnight hover:bg-xv-midnight hover:text-xv-ivory"
                        }`}
                    >
                        {t("nav.cta")}
                        <span className="block w-3 h-px bg-current transition-all duration-500 group-hover:w-6" />
                    </Link>
                </div>

                <button
                    aria-label="Menu"
                    data-testid="mobile-menu-toggle"
                    className={`md:hidden p-2 ${transparent ? "text-xv-ivory" : "text-xv-black"}`}
                    onClick={() => setOpen((v) => !v)}
                >
                    {open ? <X strokeWidth={1.2} size={24} /> : <Menu strokeWidth={1.2} size={24} />}
                </button>
            </div>

            <div
                className={`md:hidden overflow-hidden transition-[max-height] duration-700 ease-out bg-xv-ivory border-t border-xv-muted ${
                    open ? "max-h-[720px]" : "max-h-0"
                }`}
            >
                <nav className="flex flex-col px-6 py-8 gap-5">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`mobile-nav-${item.to.replace("/", "") || "home"}`}
                            className={({ isActive }) =>
                                `text-base tracking-[0.18em] uppercase font-sans-modern ${
                                    isActive ? "text-xv-gold" : "text-xv-black/80"
                                }`
                            }
                            end={item.to === "/"}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                    <div className="mt-4 pt-4 border-t border-xv-muted flex items-center justify-between">
                        <LangToggle transparent={false} />
                    </div>
                    <Link
                        to="/espace-prive"
                        data-testid="mobile-private-access"
                        className="inline-flex items-center justify-center gap-3 px-6 py-4 text-[11px] tracking-[0.28em] uppercase text-xv-midnight/80"
                    >
                        <Lock strokeWidth={1.2} size={13} />
                        {t("nav.private")}
                    </Link>
                    <Link
                        to="/contact"
                        data-testid="mobile-cta"
                        className="inline-flex items-center justify-center gap-3 px-6 py-4 text-[11px] tracking-[0.28em] uppercase border border-xv-midnight text-xv-midnight"
                    >
                        {t("nav.cta")}
                    </Link>
                </nav>
            </div>
        </header>
    );
}
